import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField, Chip, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import chipClasses from "@mui/material/Chip/chipClasses";
import { APP_COLORS } from "../../../enums";

const isValidEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

type EmailEntry = {
  email: string;
  valid: boolean;
};

const normalizeEmailsFromValue = (initialValue: unknown): EmailEntry[] => {
  if (!initialValue || !Array.isArray(initialValue)) {
    return [];
  }

  return initialValue.map((item) => {
    const email = typeof item === "string" ? item : (item as { email?: string })?.email || "";
    return {
      email,
      valid: isValidEmail(email),
    };
  });
};

const getEmailListSignature = (entries: EmailEntry[]) =>
  entries.map((entry) => entry.email).join("\n");

const EmailChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "valid",
})<{ valid?: boolean }>(({ theme, valid = true }) => {
  const color = valid ? APP_COLORS.primary : theme.palette.error.main;

  return {
    backgroundColor: "transparent",
    color,
    border: `1px solid ${color}`,
    [`&.${chipClasses.clickable}:hover`]: {
      backgroundColor: "transparent",
      color,
      borderColor: color,
    },
    [`&.${chipClasses.focusVisible}`]: {
      backgroundColor: "transparent",
    },
    [`& .${chipClasses.deleteIcon}`]: {
      color,
      "&:hover, &:active": {
        color,
      },
    },
  };
});

export const EmailAddressBox = ({
  initialValue,
  inlineLabel = false,
  onValueChange,
  error = "",
  onBlur,
  onFocus,
  disabled = false,
}: any) => {
  const [inputValue, setInputValue] = useState("");
  const [emails, setEmails] = useState<EmailEntry[]>(() =>
    normalizeEmailsFromValue(initialValue)
  );

  const initialValueSignature = useMemo(
    () => getEmailListSignature(normalizeEmailsFromValue(initialValue)),
    [initialValue]
  );

  useEffect(() => {
    const nextEmails = normalizeEmailsFromValue(initialValue);

    setEmails((prev) => {
      if (getEmailListSignature(prev) === getEmailListSignature(nextEmails)) {
        return prev;
      }

      return nextEmails;
    });
    setInputValue("");
  }, [initialValueSignature]);

  const emitValueChange = (entries: EmailEntry[]) => {
    onValueChange?.({
      list: entries.map((entry) => entry.email),
      invalidEmailIds: entries.filter((entry) => !entry.valid).map((entry) => entry.email),
    });
  };

  const addEmail = (email: string) => {
    if (email && !emails.some((entry) => entry.email === email)) {
      const valid = isValidEmail(email);
      const newEmails = [...emails, { email, valid }];
      setEmails(newEmails);
      setInputValue("");
      emitValueChange(newEmails);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      const trimmedEmail = inputValue.trim();
      if (trimmedEmail) {
        addEmail(trimmedEmail);
      }
    }
  };

  const handleBlur = () => {
    const trimmedEmail = inputValue.trim();
    if (trimmedEmail) {
      addEmail(trimmedEmail);
    }
    onBlur?.();
  };

  const handleChange = (_event: unknown, newValue: unknown) => {
    if (Array.isArray(newValue)) {
      const normalizedEmails = newValue.map((item) => {
        const email = typeof item === "string" ? item : (item as EmailEntry).email || "";
        return {
          email,
          valid: isValidEmail(email),
        };
      });
      setEmails(normalizedEmails);
      emitValueChange(normalizedEmails);
    }
  };

  return (
    <Stack gap={1}>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        size="small"
        disabled={disabled}
        onFocus={onFocus}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        value={emails}
        onChange={handleChange}
        getOptionLabel={(option: EmailEntry | string) =>
          typeof option === "string" ? option : option.email
        }
        renderTags={(value: EmailEntry[], getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });

            return (
              <EmailChip
                key={key}
                label={option.email}
                size="small"
                variant="outlined"
                valid={option.valid}
                {...tagProps}
                clickable={false}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Type and press enter"
            error={!!error}
            disabled={disabled}
            multiline
          />
        )}
      />
    </Stack>
  );
};
